import { keyframes } from "@emotion/react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Divider, Stack, Typography } from "@mui/material";
import React from "react";
import { ReactComponent as ArchiveIcon } from "../../../../../assets/archive.svg";
import { ReactComponent as GroupAddIcon } from "../../../../../assets/group-add.svg";
import { ReactComponent as PersonCheckIcon } from "../../../../../assets/person-check.svg";
import { ReactComponent as RefreshIcon } from "../../../../../assets/refresh.svg";
import {
  UserImportJobExecuteReason,
  UserImportJobExecuteResult,
  UserImportJobExecuteSummary,
} from "../../../../../services/api/types/users/userActions";
import {
  brand,
  error as errorColors,
  gray,
  success as successColors,
  warning as warningColors,
} from "../../../../../theme/themePrimitives";

const successPulse = keyframes({
  "0%": {
    boxShadow: `0 0 0 0px ${successColors[200]}, 0 0 0 0px ${successColors[100]}`,
  },
  "60%": {
    boxShadow: `0 0 0 10px ${successColors[100]}, 0 0 0 20px ${successColors[50]}`,
  },
  "100%": {
    boxShadow: `0 0 0 10px transparent, 0 0 0 20px transparent`,
  },
});

type SectionType = "new" | "updates" | "archive" | "reactivate";

interface ExecutionSection {
  type: SectionType;
  label: string;
  iconBgColor: string;
  iconColor: string;
  textColor: string;
  total: number;
  succeeded: number;
  failed: number;
  skipped: boolean;
  skippedMessage: string;
  failedUsers: UserImportJobExecuteReason[];
}

const SECTION_CONFIG: {
  type: SectionType;
  summaryKey: keyof UserImportJobExecuteSummary;
  label: string;
  iconBgColor: string;
  iconColor: string;
  textColor: string;
}[] = [
  {
    type: "new",
    summaryKey: "new",
    label: "New Users Added",
    iconBgColor: successColors[50],
    iconColor: successColors[600],
    textColor: successColors[600],
  },
  {
    type: "updates",
    summaryKey: "updates",
    label: "Users Updated",
    iconBgColor: brand[50],
    iconColor: brand[600],
    textColor: brand[600],
  },
  {
    type: "archive",
    summaryKey: "archived",
    label: "Users Archived",
    iconBgColor: errorColors[50],
    iconColor: errorColors[600],
    textColor: errorColors[600],
  },
  {
    type: "reactivate",
    summaryKey: "reactivated",
    label: "Users Reactivated",
    iconBgColor: warningColors[50],
    iconColor: warningColors[600],
    textColor: warningColors[600],
  },
];

const buildSections = (
  result: UserImportJobExecuteResult | null,
): ExecutionSection[] => {
  if (!result) return [];
  return SECTION_CONFIG.map((cfg) => {
    const s = result.summary[cfg.summaryKey];
    return {
      type: cfg.type,
      label: cfg.label,
      iconBgColor: cfg.iconBgColor,
      iconColor: cfg.iconColor,
      textColor: cfg.textColor,
      total: s.added + s.failed,
      succeeded: s.added,
      failed: s.failed,
      skipped: !!s.message,
      skippedMessage: s.message,
      failedUsers: s.reasons,
    };
  });
};

const sectionIcon = (type: SectionType) => {
  const style = { width: 24, height: 24 };
  switch (type) {
    case "new":
      return <GroupAddIcon style={style} />;
    case "updates":
      return <PersonCheckIcon style={style} />;
    case "archive":
      return <ArchiveIcon style={style} />;
    case "reactivate":
      return <RefreshIcon style={style} />;
  }
};

interface ConfirmAndExecuteProps {
  result: UserImportJobExecuteResult | null;
}

export const ConfirmAndExecute: React.FC<ConfirmAndExecuteProps> = ({
  result,
}) => {
  const sections = buildSections(result);

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            bgcolor: successColors[600],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            animation: `${successPulse} 2s ease-out infinite`,
          }}
        >
          <CheckIcon sx={{ color: "#fff", fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Bulk execution complete
          </Typography>
          <Typography variant="body2" color="text.secondary">
            See per-action results below.
          </Typography>
        </Box>
      </Stack>

      {/* Stat cards */}
      <Stack direction="row" spacing={2}>
        {sections.map((section) => (
          <StatCard key={section.type} section={section} />
        ))}
      </Stack>

      {/* Detail cards */}
      <Stack spacing={2}>
        {sections.map((section) => (
          <SectionDetailCard key={section.type} section={section} />
        ))}
      </Stack>
    </Stack>
  );
};

interface StatCardProps {
  section: ExecutionSection;
}

const StatCard: React.FC<StatCardProps> = ({ section }) => {
  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: "background.paper",
        borderRadius: "12px",
        p: 2,
      }}
    >
      <Typography variant="body2" fontWeight={600} color={section.textColor}>
        {section.label}
      </Typography>
      {section.skipped ? (
        <>
          <Box
            sx={{ width: 10, height: 4, backgroundColor: gray[400], mt: 2 }}
          />
          <Typography variant="body2" color={gray[400]} sx={{ mt: 1.5 }}>
            Skipped
          </Typography>
        </>
      ) : (
        <>
          <Typography
            variant="h3"
            fontWeight={600}
            color={section.textColor}
            sx={{ mt: 1, lineHeight: 1 }}
          >
            {section.total}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CheckIcon
                sx={{ color: successColors[600], fontSize: 16 }}
                strokeWidth={3}
              />
              <Typography
                variant="body2"
                color={successColors[600]}
                fontWeight={500}
              >
                {section.succeeded}
              </Typography>
            </Stack>
            {section.failed > 0 && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CloseIcon
                  sx={{ color: errorColors[600], fontSize: 16 }}
                  strokeWidth={3}
                />
                <Typography
                  variant="body2"
                  color={errorColors[600]}
                  fontWeight={500}
                >
                  {section.failed}
                </Typography>
              </Stack>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
};

interface SectionDetailCardProps {
  section: ExecutionSection;
}

const SectionDetailCard: React.FC<SectionDetailCardProps> = ({ section }) => {
  const showFailedList = section.failedUsers.length > 0;

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: "12px",
        p: 3,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "8px",
              bgcolor: section.iconBgColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {sectionIcon(section.type)}
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {section.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {section.skipped
                ? section.skippedMessage
                : `${section.total} records in batch`}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          {section.skipped ? (
            <StatusPill
              label="Skipped"
              bgColor={gray[100]}
              textColor={gray[400]}
              borderColor={gray[200]}
            />
          ) : (
            <>
              {section.succeeded > 0 && (
                <StatusPill
                  icon={<CheckIcon sx={{ fontSize: 14 }} />}
                  label={`${section.succeeded} succeeded`}
                  bgColor={successColors[50]}
                  textColor={successColors[600]}
                  borderColor={successColors[200]}
                />
              )}
              {section.failed > 0 && (
                <StatusPill
                  icon={<CloseIcon sx={{ fontSize: 14 }} />}
                  label={`${section.failed} failed`}
                  bgColor={errorColors[50]}
                  textColor={errorColors[600]}
                  borderColor={errorColors[200]}
                />
              )}
            </>
          )}
        </Stack>
      </Stack>
      {showFailedList && (
        <>
          <Divider sx={{ my: 2.5 }} />
          <Typography
            variant="body2"
            fontWeight={500}
            color={errorColors[600]}
            sx={{ mb: 2 }}
          >
            Failed records ({section.failedUsers.length})
          </Typography>
          <Stack spacing={2}>
            {section.failedUsers.map((user, i) => (
              <FailedUserRow key={`${user.email}-${i}`} user={user} />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

interface StatusPillProps {
  icon?: React.ReactNode;
  label: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}

const StatusPill: React.FC<StatusPillProps> = ({
  icon,
  label,
  bgColor,
  textColor,
  borderColor,
}) => (
  <Box
    sx={{
      bgcolor: bgColor,
      color: textColor,
      borderRadius: "999px",
      border: `1px solid ${borderColor}`,
      px: 1.5,
      py: 0.5,
      display: "flex",
      alignItems: "center",
      gap: 0.5,
    }}
  >
    {icon}
    <Typography variant="caption" fontWeight={500}>
      {label}
    </Typography>
  </Box>
);

interface FailedUserRowProps {
  user: UserImportJobExecuteReason;
}

const getInitials = (name: string, email: string): string => {
  const fromName = name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("");
  return (fromName || email[0] || "?").toUpperCase();
};

const FailedUserRow: React.FC<FailedUserRowProps> = ({ user }) => {
  const initials = getInitials(user.name, user.email);

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: errorColors[50],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" fontWeight={600} color={errorColors[600]}>
            {initials}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>
            {user.name || user.email}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </Box>
      </Stack>
      <Box
        sx={{
          bgcolor: errorColors[50],
          border: `1px solid ${errorColors[100]}`,
          borderRadius: "8px",
          px: 1.5,
          py: 0.5,
          maxWidth: "50%",
        }}
      >
        <Typography variant="caption" fontWeight={500} color={errorColors[600]}>
          {user.message}
        </Typography>
      </Box>
    </Stack>
  );
};
